import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import BrandLogo from "./BrandLogo";
import { useT } from "../i18n";
import { LAUNCH_BUTTON_LABEL, requestFirstCountStart } from "../lib/ceremony";
import { CELEBRATION_MS, fitInaugurationFrame, startCelebration } from "./inaugurationFx";
import "./Inauguration.css";

const COUNT_MS = 1000;

function Celebration({ className, testId, durationMs = 0, onDone, spareRef }) {
  const canvasRef = useRef(null);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    return startCelebration(canvas, {
      durationMs,
      spareEl: spareRef ? () => spareRef.current : null,
      onDone: () => {
        if (doneRef.current) doneRef.current();
      },
    });
  }, [durationMs, spareRef]);

  return <canvas ref={canvasRef} className={className} data-testid={testId} aria-hidden="true" />;
}

export function InaugurationGate({ onComplete }) {
  const { t } = useT();
  const [step, setStep] = useState("ready");
  const doneRef = useRef(onComplete);
  const buttonRef = useRef(null);
  const gateRef = useRef(null);
  const frameRef = useRef(null);
  doneRef.current = onComplete;

  useEffect(() => {
    buttonRef.current?.focus();
  }, []);

  useLayoutEffect(() => {
    const gate = gateRef.current;
    const frame = frameRef.current;
    if (!gate || !frame) return undefined;
    const fit = () => fitInaugurationFrame(gate, frame);
    fit();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(fit) : null;
    if (observer) observer.observe(gate);
    window.addEventListener("resize", fit);
    let cancelFonts = false;
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        if (!cancelFonts) fit();
      });
    }
    return () => {
      cancelFonts = true;
      if (observer) observer.disconnect();
      window.removeEventListener("resize", fit);
    };
  }, [step]);

  useEffect(() => {
    if (step !== 3 && step !== 2 && step !== 1) return undefined;
    const timer = window.setTimeout(() => {
      if (step === 1) {
        requestFirstCountStart();
        if (doneRef.current) doneRef.current();
        return;
      }
      setStep(step - 1);
    }, COUNT_MS);
    return () => window.clearTimeout(timer);
  }, [step]);

  return (
    <div className="inauguration-gate" role="dialog" aria-modal="true" aria-label="Website inauguration" data-testid="inauguration-gate" ref={gateRef}>
      <div className="inaug-frame" ref={frameRef}>
        <img className="inaug-emblem" src="/img/ap-emblem.png" alt="Government of Andhra Pradesh" />
        <p className="inaug-kicker">{t("enterprise")}</p>
        <div className="inaug-logo">
          <BrandLogo size="auto" alt="APMERCL" testId="inauguration-logo" />
        </div>
        <h1 className="inaug-title">{t("title")}</h1>
        <div className="inaug-rule" aria-hidden="true" />
        <p className="inaug-sub">Official website inauguration</p>
        {step === "ready" ? (
          <button
            ref={buttonRef}
            type="button"
            className="inaug-launch"
            data-testid="inauguration-launch"
            onClick={() => setStep(3)}
          >
            {LAUNCH_BUTTON_LABEL}
          </button>
        ) : (
          <div className="inaug-count" data-testid="inauguration-count" aria-live="assertive">
            <span className="inaug-sr">{`Opening in ${step}`}</span>
            <span aria-hidden="true">{step}</span>
          </div>
        )}
      </div>
      <Celebration className="inaug-fx" testId="inauguration-gate-fx" spareRef={frameRef} />
    </div>
  );
}

export function EdgeFireworks({ onDone }) {
  return (
    <Celebration
      className="inaug-fireworks"
      testId="inauguration-fireworks"
      durationMs={CELEBRATION_MS}
      onDone={onDone}
    />
  );
}
